"use client";

import { useEffect, useRef, useState } from "react";
import type { CardComponentProps } from "onborda";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function TourCard({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  arrow,
}: CardComponentProps) {
  const isLastStep = currentStep === totalSteps - 1;
  const cardRef = useRef<HTMLDivElement>(null);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<string>('500px');
  const [dynamicMaxWidth, setDynamicMaxWidth] = useState<string>('500px');
  const [dynamicWidth, setDynamicWidth] = useState<string>('500px');

  // Dynamically calculate available space and adjust card dimensions
  useEffect(() => {
    const calculateDimensions = () => {
      if (!cardRef.current) return;

      const card = cardRef.current;
      const rect = card.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Calculate overflow on all sides (with 40px margin)
      const rightOverflow = rect.right - (viewportWidth - 40);
      const bottomOverflow = rect.bottom - (viewportHeight - 40);
      const leftOverflow = 40 - rect.left;
      const topOverflow = 40 - rect.top;
      
      let widthChanged = false;
      let heightChanged = false;
      
      // Handle horizontal overflow
      if (rightOverflow > 0 || leftOverflow > 0) {
        // Calculate available width from current position
        const spaceFromLeft = Math.max(40, rect.left);
        // Allow card to shrink below 300px if necessary to fit
        const availableWidth = Math.max(250, viewportWidth - spaceFromLeft - 60);
        
        // If even the minimum doesn't fit, calculate based on actual available space
        const actualAvailableWidth = viewportWidth - rect.left - 40;
        const finalWidth = Math.max(200, Math.min(availableWidth, actualAvailableWidth));
        
        const newWidth = `${finalWidth}px`;
        setDynamicMaxWidth(newWidth);
        setDynamicWidth(newWidth);
        widthChanged = true;
        
        // Also force it via direct style manipulation
        if (card.style) {
          card.style.maxWidth = newWidth;
          card.style.width = newWidth;
        }
      } else {
        if (!widthChanged) {
          setDynamicMaxWidth('min(500px, calc(100vw - 80px))');
          setDynamicWidth('500px'); // Set explicit width to expand the card
        }
      }
      
      // Handle vertical overflow
      if (bottomOverflow > 0 || topOverflow > 0) {
        const spaceFromTop = Math.max(40, rect.top);
        const availableHeight = Math.max(250, viewportHeight - spaceFromTop - 80);
        const newHeight = `${availableHeight}px`;
        setDynamicMaxHeight(newHeight);
        heightChanged = true;
        
        // Also force it via direct style manipulation
        if (card.style) {
          card.style.maxHeight = newHeight;
          card.style.height = 'auto';
        }
      } else if (!heightChanged) {
        setDynamicMaxHeight('min(500px, calc(100vh - 100px))');
      }
    };

    // Multiple triggers to ensure calculation happens
    calculateDimensions();
    
    // Delayed calculations to catch Onborda positioning
    const timer1 = setTimeout(calculateDimensions, 50);
    const timer2 = setTimeout(calculateDimensions, 150);
    const timer3 = setTimeout(calculateDimensions, 300);
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateDimensions);
    
    // Watch for position changes using MutationObserver
    const mutationObserver = new MutationObserver(calculateDimensions);
    if (cardRef.current) {
      mutationObserver.observe(cardRef.current, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }
    
    // Use ResizeObserver for size changes
    const resizeObserver = new ResizeObserver(calculateDimensions);
    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    // Watch parent for position changes (Onborda wrapper)
    const parent = cardRef.current?.parentElement;
    let parentMutationObserver: MutationObserver | null = null;
    if (parent) {
      parentMutationObserver = new MutationObserver(calculateDimensions);
      parentMutationObserver.observe(parent, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => {
      window.removeEventListener('resize', calculateDimensions);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      parentMutationObserver?.disconnect();
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [currentStep]); // Re-run when step changes

  const handleFinish = () => {
    nextStep();
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('onborda-tour-complete'));
      window.dispatchEvent(new CustomEvent('user-tour-complete'));
    }, 100);
  };

  return (
    <Card 
      ref={cardRef}
      className="shadow-2xl border-2 border-primary bg-background"
      style={{
        position: 'fixed',
        zIndex: 100002,
        pointerEvents: 'auto',
        maxHeight: dynamicMaxHeight,
        maxWidth: dynamicMaxWidth,
        width: dynamicWidth,
        height: 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader className="relative pb-2 flex-shrink-0 border-b border-border/50 p-4">
        <div className="flex items-start gap-2">
          {step.icon && (
            <div className="flex-shrink-0 rounded-full bg-primary/10 p-1.5 text-primary">
              {step.icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">{step.title}</CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Step {currentStep + 1} of {totalSteps}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <CardContent className="py-3 px-4">
          <div className="text-sm text-muted-foreground leading-relaxed">
            {step.content}
          </div>
          <div className="pointer-events-none mt-2">
            {arrow}
          </div>
        </CardContent>
      </div>
      
      {/* Fixed buttons at bottom */}
      {step.showControls && (
        <div className="flex-shrink-0 border-t border-border/50 p-4 bg-background">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevStep}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {isLastStep ? (
                <Button
                  size="sm"
                  onClick={handleFinish}
                >
                  Finish Tour
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={nextStep}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

